import classNames from 'classnames';
import { Map } from 'immutable';
import { useBrandFontsForRelation } from 'modules/BrandDefinition/hooks';
import React, { useRef } from 'react';
import { ConnectDragSource } from 'react-dnd';
import { useSelector } from 'react-redux';
import * as Constants from 'const';
import ArtboardLayoutActions from 'containers/ArtboardLayoutActions';
import * as documentsSelectors from 'containers/Documents/selectors';
import * as projectSelectors from 'containers/Project/selectors';
import { useBrandColorsForLayout } from 'hooks/useBrandColorsForLayout';
import { useLayoutRegularFirstLayeredRelations } from 'hooks/useLayoutRegularFirstRelation';
import { useUpdateCellHeight } from 'hooks/useUpdateCellsHeight';
import * as Models from 'models';
import { selectAbbreviationsData } from '../../store/selectors';
import { AbbreviationsListLayeredRelation, AbbreviationsListRelationStylesMap } from '../../types';
import { abbreviationsListsFromContext } from '../../utils/abbreviationsListFromContext';
import List from './components/List';
import Toolbar from './components/Toolbar';
import { useAbbreviationsLayout } from './hooks/useAbbreviationsLayout';
import { useOnResize } from './hooks/useOnResize';
import { useStyles } from './hooks/useStyles';
import css from './styles.module.scss';

type Props = {
  artboardLayoutContainer?: HTMLDivElement;
  connectDragSource: ConnectDragSource;
  layout: Models.LayoutMap;
  dataTestId?: string;
};

const AbbreviationsListLayout: React.FunctionComponent<Props> = (props) => {
  const { artboardLayoutContainer, connectDragSource, layout, dataTestId = 'abbreviationListLayout' } = props;

  const projectType = useSelector(projectSelectors.projectType);
  const projectConfig = Constants.ProjectsConfig[projectType];
  const activeLayer = useSelector(projectSelectors.activeLayer);
  // AbbreviationsList stores styles on original layer only (in the same way as references element)
  const activeLayerForStyles = Constants.Layer.ORIGINAL;
  const relations = useSelector(documentsSelectors.relationsOnScreens);
  const documents = useSelector(documentsSelectors.documentsByEntityType);
  const abbreviationsData = useSelector(selectAbbreviationsData);

  const relation = useLayoutRegularFirstLayeredRelations<AbbreviationsListLayeredRelation>(layout);
  const colors = useBrandColorsForLayout(layout.get('id'));
  const fonts = useBrandFontsForRelation(relation.get('id'));

  const abbreviationsListsByRelationId = abbreviationsListsFromContext(
    { activeLayer, relations, documents, abbreviations: abbreviationsData, projectType },
  );
  const abbreviations = abbreviationsListsByRelationId[relation.get('id')] || {};

  // Curently there is a lot of refactoring is needed to define ref outside of the useAbbreviationsLayout hook
  const containerSecondRef = useRef<HTMLDivElement>();

  useUpdateCellHeight(
    relation.get('id'),
    containerSecondRef.current,
  );

  const [styles, stylesSetters, stylesCSS] = useStyles(
    layout.get('styles'),
    relation.getIn(['styles', activeLayerForStyles]) || Map({}) as AbbreviationsListRelationStylesMap,
    {
      colors,
      fonts,
      images: documents.get(Constants.DocumentsKey.IMAGES),
      containerRef: containerSecondRef,
    },
    projectConfig,
  );

  const { containerRef, editMode, enableEditMode } = useAbbreviationsLayout(
    artboardLayoutContainer,
    layout,
    relation,
    activeLayerForStyles,
    styles,
  );

  const onResize = useOnResize(
    containerRef,
    styles,
    stylesSetters,
  );

  const isBlank = !abbreviations.length;

  return (
    <>
      <div
        className={classNames(css.container, {
          [css.blank]: isBlank,
          [css.scrollable]: styles.scrollable,
        })}
        style={stylesCSS}
        ref={(element: HTMLDivElement): void => {
          containerSecondRef.current = element;
          containerRef.current = element;
        }}
        data-testid={dataTestId}
      >
        <List list={abbreviations} dataTestId={`${dataTestId}-abbreviations`} />
      </div>
      {
        editMode &&
        <Toolbar
          styles={styles}
          stylesSetters={stylesSetters}
          projectConfig={projectConfig}
          layoutId={layout.get('id')}
        />
      }
      <ArtboardLayoutActions
        connectDragSource={connectDragSource}
        layout={layout}
        height={styles.height}
        enableLayoutEditMode={enableEditMode}
        editMode={editMode}
        isResizeAvailable={styles.scrollable}
        handleDrag={onResize}
        dataTestId={`${dataTestId}-actions`}
      />
    </>
  );
};

export default AbbreviationsListLayout;
